import { type ParentComponent, Show, untrack } from "solid-js";
import { Icon, type IconName } from "../icon/Icon";
import { Collapsible } from "@ark-ui/solid";
import { isNil } from "lodash-es";
import Text from "../text/Text";
import { condition } from "../../utils/solidUtils";
import { createWritableMemo } from "@solid-primitives/memo";
import type { Status } from "../../types";
import { STATUSES } from "../../types";

type InlineAccordionProps = {
  expandLabel: string;
  collapseLabel: string;
  icon?: IconName;
  open?: boolean;
  variant?: Status | "error";
  setOpen?: () => boolean;
};

const getIsDefaultVariant = (variant?: string | null) => {
  if (isNil(variant)) {
    return true;
  }
  return !STATUSES.includes(variant);
};

const InlineAccordion: ParentComponent<InlineAccordionProps> = (props) => {
  const isDefaultVariant = getIsDefaultVariant(untrack(() => props.variant));
  const [open, setOpen] = createWritableMemo<boolean>(() => props.open ?? false);
  const textClasses = () => ({
    "text-secondary": isDefaultVariant,
    "text-status-warning": props.variant === "warning",
    "text-status-error": props.variant === "error",
    "text-status-danger": props.variant === "danger",
    "text-status-informative": props.variant === "informative",
    "text-status-upgrade": props.variant === "upgrade",
  });
  const iconClasses = () => ({
    "icon-secondary": isDefaultVariant,
    "icon-status-warning": props.variant === "warning",
    "icon-status-error": props.variant === "error",
    "icon-status-danger": props.variant === "danger",
    "icon-status-informative": props.variant === "informative",
    "icon-status-upgrade": props.variant === "upgrade",
  });

  return (
    <Collapsible.Root
      class="w-full"
      open={open()}
      onOpenChange={(details) => {
        if (isNil(props?.setOpen)) {
          setOpen(details.open);
        } else {
          setOpen(props.setOpen() === true);
        }
      }}
    >
      <Collapsible.Context>
        {(context) => (
          <>
            <Collapsible.Trigger class="flex max-w-full items-center gap-1 text-small-medium focus-visible:outline-default">
              <Show when={condition(props.icon, (icon) => !isNil(icon))}>
                {(icon) => <Icon name={icon()} type="solid" size="sm" class="shrink-0" classList={iconClasses()} />}
              </Show>
              <Text
                as="span"
                truncate
                text={context().open ? props.collapseLabel : props.expandLabel}
                classList={textClasses()}
              />
              <div
                class="shrink-0 transition-transform duration-200 ease-linear"
                classList={{ "rotate-180": context().open }}
              >
                <Icon name="chevronDown" type="solid" size="sm" classList={iconClasses()} />
              </div>
            </Collapsible.Trigger>
            {/* oxlint-disable better-tailwindcss/no-restricted-classes */}
            <Collapsible.Content class="grid grid-rows-[1fr] transition-all duration-250 ease-out closed:grid-rows-[0fr] closed:duration-200 closed:ease-in">
              <div class="overflow-hidden">{props.children}</div>
            </Collapsible.Content>
          </>
        )}
      </Collapsible.Context>
    </Collapsible.Root>
  );
};

export default InlineAccordion;
